import {
  BottomSheet,
  Card,
  CbhIcon,
  PageHeaderWrapper,
  Text,
  Title,
  type UseModalState,
} from "@clipboard-health/ui-components";
import { isDefined } from "@clipboard-health/util-ts";
import { CardContent, DialogContent, Stack } from "@mui/material";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { StaticDatePicker } from "@mui/x-date-pickers/StaticDatePicker";
import { Button } from "@src/appV2/redesign/components/Button";
import { DialogFooter } from "@src/appV2/redesign/components/DialogFooter";
import { isBefore, startOfDay } from "date-fns";
import { useState } from "react";

interface ConfirmVisitOptionsBottomSheetProps {
  modalState: UseModalState;
  visitDate: Date | undefined;
  onConfirm: (date: Date) => void;
}

export function ConfirmVisitOptionsBottomSheet(props: ConfirmVisitOptionsBottomSheetProps) {
  const { modalState, visitDate, onConfirm } = props;
  const [selectedDate, setSelectedDate] = useState<Date | undefined>(visitDate);
  const startOfToday = startOfDay(new Date());
  const isPreviousVisit = isDefined(selectedDate) && isBefore(selectedDate, startOfToday);
  const confirmButtonText = isPreviousVisit ? "Log past date" : "Next";

  return (
    <BottomSheet
      modalState={modalState}
      footer={
        <DialogFooter orientation="vertical" onClose={modalState.closeModal}>
          <Button
            fullWidth
            variant="contained"
            disabled={!selectedDate}
            onClick={() => {
              if (!isDefined(selectedDate)) {
                return;
              }

              onConfirm(selectedDate);
            }}
          >
            {confirmButtonText}
          </Button>
        </DialogFooter>
      }
    >
      <DialogContent>
        <Stack spacing={4} alignItems="center">
          <PageHeaderWrapper variant="tertiary" />
          <Title semibold variant="h3" component="h3">
            Select visit date
          </Title>

          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <StaticDatePicker
              value={selectedDate}
              slotProps={{
                calendarHeader: { sx: { display: "flex", justifyContent: "space-around" } },
                toolbar: { hidden: true },
                actionBar: { hidden: true },
              }}
              sx={(theme) => ({ backgroundColor: theme.palette.background.tertiary })}
              onChange={(newValue) => {
                setSelectedDate(newValue ?? undefined);
              }}
            />
          </LocalizationProvider>
          {isPreviousVisit && (
            <Card
              sx={{
                backgroundColor: (theme) => theme.palette.intent?.warning.background,
                color: (theme) => theme.palette.intent?.warning.text,
              }}
            >
              <CardContent>
                <Stack direction="row" spacing={2}>
                  <CbhIcon type="triangle-exclamation" size="small" />
                  <Text variant="body2">
                    This date has passed. Would you like to log a previous visit?
                  </Text>
                </Stack>
              </CardContent>
            </Card>
          )}
        </Stack>
      </DialogContent>
    </BottomSheet>
  );
}
